import { BaseService } from './BaseService';
import { API_ENDPOINTS } from './config';
import { PrimeProcess } from '../../types/primeModel';
import { ProcessCentricProcess } from '../../types/primeModelViews';

export class ProcessService extends BaseService {
  static async getAll(): Promise<PrimeProcess[]> {
    return this.get<PrimeProcess[]>(API_ENDPOINTS.PRIME_PROCESSES.BASE);
  }

  static async getById(id: string): Promise<PrimeProcess> {
    return this.get<PrimeProcess>(API_ENDPOINTS.PRIME_PROCESSES.BY_ID(id));
  }

  static async getByArtifactId(artifactId: string): Promise<PrimeProcess[]> {
    return this.get<PrimeProcess[]>(API_ENDPOINTS.PRIME_PROCESSES.BY_ARTIFACT(artifactId));
  }

  static async create(data: Partial<PrimeProcess>): Promise<PrimeProcess> {
    // Extract the artifact ID from the data
    const artifactId = data.prime_artifact_ref;
    if (!artifactId) {
      throw new Error('prime_artifact_ref is required for creating a process');
    }

    // Note: Backend validation requires prime_artifact_ref in request body
    // even though the endpoint removes it and uses URL path artifact_id
    return this.post<PrimeProcess>(API_ENDPOINTS.PRIME_PROCESSES.BY_ARTIFACT(artifactId), data);
  }

  static async update(id: string, data: Partial<PrimeProcess>): Promise<PrimeProcess> {
    // Get the artifact ID from the data
    const artifactId = data.prime_artifact_ref;
    if (!artifactId) {
      throw new Error('prime_artifact_ref is required for updating a process');
    }
    return this.put<PrimeProcess>(`/prime-artifacts/${artifactId}/processes/${id}`, data);
  }

  static async delete(id: string): Promise<void> {
    return this.delete(API_ENDPOINTS.PRIME_PROCESSES.BY_ID(id));
  }

  static async searchByName(name: string): Promise<PrimeProcess[]> {
    return this.get<PrimeProcess[]>(API_ENDPOINTS.PRIME_PROCESSES.SEARCH(name));
  }

  static async getTransitions(processId: string): Promise<PrimeProcess[]> {
    return this.get<PrimeProcess[]>(API_ENDPOINTS.PRIME_PROCESSES.TRANSITIONS(processId));
  }

  static async getProcessCentricView(processId: string): Promise<ProcessCentricProcess> {
    try {
      const response = await this.get<ProcessCentricProcess>(
        API_ENDPOINTS.PRIME_PROCESSES.PROCESS_VIEW(processId)
      );

      // Validate the response
      if (!response.id || !response.name || !response.prime_artifact_ref) {
        throw new Error('Invalid process-centric view response');
      }

      // Validate input and output states
      if (!Array.isArray(response.input_states) || !Array.isArray(response.output_states)) {
        throw new Error('Invalid process-centric view: missing input or output states');
      }

      return response;
    } catch (error) {
      console.error('Error fetching process-centric view:', error);
      throw error;
    }
  }
}
